import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer con conferma maggiore età 18+ obbligatoria.
 *
 * - Inserisce automaticamente la checkbox 18+ nel riepilogo del cart drawer
 * - Disabilita Checkout / Shop Pay / Google Pay finché non viene spuntata
 * - Salva nel carrello l'attributo "Conferma 18+ = Sì"
 * - Riapplica il controllo dopo i render dinamici del carrello
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #ageObserver = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#installAgeObserver();
    this.#setupAgeGate();

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#ageObserver?.disconnect();
    this.#ageObserver = null;
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#setupAgeGate();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#setupAgeGate();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-drawer] Event promise rejected:', error);
        }
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }

  #installAgeObserver() {
    if (this.#ageObserver) return;

    this.#ageObserver = new MutationObserver(() => {
      this.#setupAgeGate();
    });

    this.#ageObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  #setupAgeGate() {
    if (this.#isCartEmpty()) return;

    const summary = this.querySelector('.cart-drawer__summary');

    if (!summary) return;

    let gate = summary.querySelector('[data-age-gate]');

    if (!gate) {
      gate = document.createElement('div');
      gate.setAttribute('data-age-gate', '');
      gate.className = 'sexy-age-gate';

      gate.innerHTML = `
        <label class="sexy-age-gate__label">
          <input
            type="checkbox"
            class="sexy-age-gate__checkbox"
            data-age-checkbox
            aria-label="Confermo di avere almeno 18 anni"
          >

          <span class="sexy-age-gate__check" aria-hidden="true">✓</span>

          <span class="sexy-age-gate__copy">
            <strong>🔞 Conferma maggiore età</strong>

            <span>
              Dichiaro di avere compiuto almeno
              <strong>18 anni</strong>
              e di essere maggiorenne.
            </span>
          </span>
        </label>

        <div
          class="sexy-age-gate__error"
          data-age-error
          role="alert"
          aria-live="polite"
          hidden
        >
          Per procedere devi confermare di avere almeno 18 anni.
        </div>
      `;

      const checkout = this.#findPrimaryCheckout(summary);

      if (checkout) {
        checkout.parentElement?.insertBefore(gate, checkout);
      } else {
        summary.prepend(gate);
      }

      this.#injectAgeStyles();

      const checkbox = gate.querySelector('[data-age-checkbox]');

      checkbox?.addEventListener('change', () => {
        this.#syncAgeState();
        this.#saveAgeAttribute(Boolean(checkbox.checked));
      });
    }

    this.#syncAgeState();
  }

  /**
   * @param {Element} scope
   */
  #findPrimaryCheckout(scope) {
    return (
      scope.querySelector('button[name="checkout"]') ||
      scope.querySelector('input[name="checkout"]') ||
      scope.querySelector('[data-checkout]') ||
      scope.querySelector('a[href*="/checkout"]')
    );
  }

  #checkoutControls() {
    return this.querySelectorAll(
      [
        'button[name="checkout"]',
        'input[name="checkout"]',
        '[data-checkout]',
        'a[href*="/checkout"]',
      ].join(',')
    );
  }

  #syncAgeState() {
    const gate = this.querySelector('[data-age-gate]');
    const checkbox = gate?.querySelector('[data-age-checkbox]');
    const error = gate?.querySelector('[data-age-error]');

    if (!(checkbox instanceof HTMLInputElement)) return;

    const confirmed = checkbox.checked;

    this.toggleAttribute('age-confirmed', confirmed);

    this.#checkoutControls().forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
      ) {
        control.disabled = !confirmed;
      }

      control.setAttribute(
        'aria-disabled',
        confirmed ? 'false' : 'true'
      );

      control.classList.toggle('sexy-age-disabled', !confirmed);
    });

    const accelerated = this.querySelectorAll(
      [
        'shopify-accelerated-checkout',
        'shopify-accelerated-checkout-cart',
        '.additional-checkout-buttons',
        '.shopify-payment-button',
        '[data-shopify="payment-button"]',
        '.cart__dynamic-checkout-buttons',
      ].join(',')
    );

    accelerated.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.pointerEvents = confirmed ? '' : 'none';
        element.style.opacity = confirmed ? '' : '0.38';
      }
    });

    if (confirmed) {
      gate?.classList.remove('is-invalid');

      if (error instanceof HTMLElement) {
        error.hidden = true;
      }
    }
  }

  /**
   * @param {boolean} confirmed
   */
  async #saveAgeAttribute(confirmed) {
    try {
      const root = window.Shopify?.routes?.root || '/';

      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            'Conferma 18+': confirmed ? 'Sì' : '',
          },
        }),
      });
    } catch (error) {
      console.warn(
        '[cart-drawer] Impossibile salvare conferma 18+',
        error
      );
    }
  }

  #showAgeError() {
    const gate = this.querySelector('[data-age-gate]');
    const checkbox = gate?.querySelector('[data-age-checkbox]');
    const error = gate?.querySelector('[data-age-error]');

    gate?.classList.add('is-invalid');

    if (error instanceof HTMLElement) {
      error.hidden = false;
    }

    gate?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    if (checkbox instanceof HTMLInputElement) {
      setTimeout(() => checkbox.focus(), 250);
    }
  }

  #injectAgeStyles() {
    if (document.getElementById('sexy-age-gate-styles')) return;

    const style = document.createElement('style');
    style.id = 'sexy-age-gate-styles';

    style.textContent = `
      .sexy-age-gate {
        margin: 0 0 14px;
        padding: 14px 15px;
        border: 1px solid rgba(240, 0, 114, .24);
        border-radius: 14px;
        background:
          linear-gradient(
            135deg,
            rgba(240, 0, 114, .07),
            rgba(0, 0, 0, .025)
          );
        transition: .2s ease;
      }

      .sexy-age-gate__label {
        display: flex;
        align-items: flex-start;
        gap: 11px;
        cursor: pointer;
        user-select: none;
      }

      .sexy-age-gate__checkbox {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
      }

      .sexy-age-gate__check {
        display: grid;
        place-items: center;
        flex: 0 0 27px;
        width: 27px;
        height: 27px;
        margin-top: 1px;
        border: 2px solid rgba(70, 70, 70, .5);
        border-radius: 7px;
        background: #fff;
        color: transparent;
        font-weight: 900;
        transition: .18s ease;
      }

      .sexy-age-gate__checkbox:checked
      + .sexy-age-gate__check {
        border-color: #f00072;
        background: #f00072;
        color: #fff;
      }

      .sexy-age-gate__copy {
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-size: 12px;
        line-height: 1.4;
      }

      .sexy-age-gate__copy > strong {
        font-size: 13px;
        font-weight: 800;
      }

      .sexy-age-gate__copy > span {
        opacity: .76;
      }

      .sexy-age-gate__error {
        margin-top: 10px;
        padding-top: 9px;
        border-top: 1px solid rgba(180, 20, 50, .14);
        color: #b0173f;
        font-size: 11px;
        font-weight: 700;
      }

      .sexy-age-gate.is-invalid {
        border-color: rgba(205, 20, 65, .7);
        background: rgba(205, 20, 65, .055);
        box-shadow:
          0 0 0 3px rgba(205, 20, 65, .07);
      }

      .sexy-age-disabled {
        opacity: .45 !important;
        cursor: not-allowed !important;
      }
    `;

    document.head.appendChild(style);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
